import { useState } from 'react'

import { BUSINESS_TIMEZONE, shortDateTime } from '../../lib/datetime'
import type { WorkspaceSummary } from '../../lib/optypes'

/**
 * What the homeowner chose, in the operator console (01 §4, WS7-06).
 *
 * This is the screen Andrew opens in the van. The order is the order of his attention: **what
 * to measure**, then **what to ask them**, then **what they picked**, then **the pictures**.
 * The list is last on purpose — it is the part he already has in the email.
 *
 * No total, here either. 01 §7 is about homeowner surfaces, but there is no total to show:
 * the studio never computed one, and a number invented on this screen would be the number he
 * quotes from.
 */

export function WorkspacePanel({ workspace }: { workspace: WorkspaceSummary }) {
  const [openRender, setOpenRender] = useState<string | null>(null)

  return (
    <section className="mt-4 rounded-card border border-line bg-white p-5">
      <div className="flex flex-wrap items-baseline justify-between gap-2">
        <h2 className="text-lg">Design studio</h2>
        <span className="font-mono text-xs uppercase tracking-[0.12em] text-muted">
          {workspace.status}
          {workspace.sent_at
            ? ` · sent ${shortDateTime(workspace.sent_at, BUSINESS_TIMEZONE)}`
            : ''}
        </span>
      </div>

      {workspace.must_verify.length > 0 && (
        <>
          <h3 className="mt-4 font-mono text-xs uppercase tracking-[0.14em] text-muted">
            Measure / verify on site
          </h3>
          <ul className="m-0 mt-2 grid list-none gap-1.5 p-0">
            {workspace.must_verify.map((item) => (
              <li key={item} className="rounded-btn bg-sand px-3 py-2 text-sm text-ink">
                {item}
              </li>
            ))}
          </ul>
        </>
      )}

      {workspace.open_questions.length > 0 && (
        <>
          <h3 className="mt-5 font-mono text-xs uppercase tracking-[0.14em] text-muted">
            Worth asking them
          </h3>
          <ul className="m-0 mt-2 grid list-none gap-1.5 p-0 text-sm text-body">
            {workspace.open_questions.map((item) => (
              <li key={item}>· {item}</li>
            ))}
          </ul>
        </>
      )}

      {workspace.lines.length > 0 && (
        <>
          <h3 className="mt-5 font-mono text-xs uppercase tracking-[0.14em] text-muted">
            What they chose
          </h3>
          <ul className="m-0 mt-2 grid list-none gap-2.5 p-0">
            {workspace.lines.map((line, index) => (
              <li key={index} className="rounded-btn border border-line p-3">
                <p className="m-0 text-xs text-muted">{line.room}</p>
                <p className="m-0 font-semibold text-ink">{line.what}</p>
                <p className="m-0 text-sm text-body">
                  {line.qty}
                  {!line.measured && (
                    <span className="text-blue-press"> · estimated from a photo</span>
                  )}
                </p>
                {line.material_price && (
                  <p className="m-0 text-sm text-body">
                    {line.material_price} at {line.store}, priced {line.price_as_of}
                  </p>
                )}
                <p className="m-0 text-sm text-body">{line.labor_from}</p>
                {line.url && (
                  <a
                    href={line.url}
                    target="_blank"
                    rel="noreferrer noopener"
                    className="text-sm font-semibold text-blue-deep underline-offset-2 hover:underline"
                  >
                    Product page
                  </a>
                )}
              </li>
            ))}
          </ul>
        </>
      )}

      {workspace.renders.length > 0 && (
        <>
          <h3 className="mt-5 font-mono text-xs uppercase tracking-[0.14em] text-muted">
            Previews they saw
          </h3>
          <ul className="m-0 mt-2 grid list-none grid-cols-2 gap-2 p-0 sm:grid-cols-3">
            {workspace.renders.map((render) => (
              <li key={render.id}>
                <button
                  type="button"
                  onClick={() => setOpenRender(openRender === render.id ? null : render.id)}
                  className="block w-full overflow-hidden rounded-btn border border-line"
                >
                  {render.thumb_url && (
                    <img
                      src={render.thumb_url}
                      alt={`Preview of ${render.room}`}
                      className="h-28 w-full object-cover"
                      loading="lazy"
                    />
                  )}
                </button>
                {openRender === render.id && render.thumb_url && (
                  <img
                    src={render.thumb_url}
                    alt={`Preview of ${render.room}, full size`}
                    className="mt-2 w-full rounded-btn"
                  />
                )}
              </li>
            ))}
          </ul>
          <p className="m-0 mt-2 text-xs text-muted">
            AI previews — the actual product and finish may vary.
          </p>
        </>
      )}

      {workspace.transcript.length > 0 && (
        <details className="mt-5">
          <summary className="cursor-pointer font-mono text-xs uppercase tracking-[0.14em] text-muted">
            What they said ({workspace.transcript.length})
          </summary>
          <ul className="m-0 mt-2 grid list-none gap-1.5 p-0 text-sm">
            {workspace.transcript.map((turn, index) => (
              <li key={index}>
                <strong className="text-ink">{turn.role === 'user' ? 'They' : 'Studio'}:</strong>{' '}
                <span className="text-body">{turn.content}</span>
              </li>
            ))}
          </ul>
        </details>
      )}

      {workspace.packet_pdf_url && (
        <a
          href={workspace.packet_pdf_url}
          className="mt-5 inline-block min-h-11 rounded-btn border border-line bg-white px-4 font-semibold leading-[2.6] text-blue-deep"
        >
          Download the packet PDF
        </a>
      )}
    </section>
  )
}
